import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  approveWorkReport,
  createWorkReport,
  deleteWorkReport,
  deleteWorkReportAttachment,
  downloadWorkReportAttachment,
  fetchWorkReport,
  fetchWorkReports,
  revokeWorkReport,
  updateWorkReport,
  uploadWorkReportAttachments,
  downloadWorkReportPdf,
} from '../../api/workReport';
import { APPROVAL_TARGET_WORK_REPORT } from '../../types/approval';
import { canConfirmInspection } from '../../types/companyRole';
import { WorkReport, WorkReportAttachment, workReportStatusClass, workReportStatusLabel } from '../../types/workReport';
import { notifyError, reportError } from '../../utils/notify';
import { useCompanyStore } from '../../store/companyStore';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import ListPager from '../common/ListPager';

import './LeaveRequestPanel.css';

type View =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; workReportId: number }
  | { type: 'edit'; workReportId: number };

const LIST_VIEW = { type: 'list' } as const;

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function formatSize(size?: number | null) {
  if (size == null) return '';
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}

function WorkReportPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveAsManager = canConfirmInspection(role);

  const [view, setView] = useState<View>(LIST_VIEW);
  const [rows, setRows] = useState<WorkReport[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [detail, setDetail] = useState<WorkReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [content, setContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<WorkReportAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadList = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      const result = await fetchWorkReports({ page: pageNum });
      setRows(result.content || []);
      setPage(result.page ?? pageNum);
      setTotalPages(result.totalPages ?? 0);
      setTotalElements(result.totalElements ?? 0);
      setError('');
    } catch {
      reportError(setError, '업무 보고서 목록을 불러오지 못했습니다.');
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setView(LIST_VIEW);
    void loadList(0);
  }, [companyId, loadList, setView]);

  const resetFormFiles = () => {
    setPendingFiles([]);
    setExistingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setTitle('');
    setReportDate(today);
    setContent('');
    setDetail(null);
    resetFormFiles();
    setView({ type: 'create' });
  };

  const openDetail = async (workReportId: number) => {
    try {
      setLoading(true);
      setDetail(await fetchWorkReport(workReportId));
      setView({ type: 'detail', workReportId });
      setError('');
    } catch {
      notifyError('업무 보고서를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row: WorkReport) => {
    setTitle(row.title || '');
    setReportDate(formatDate(row.reportDate) === '-' ? '' : formatDate(row.reportDate));
    setContent(row.content || '');
    setDetail(row);
    setPendingFiles([]);
    setExistingFiles(row.attachments ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setView({ type: 'edit', workReportId: row.workReportId });
  };

  const backToList = () => {
    setView(LIST_VIEW);
    setDetail(null);
    setApprovalOpen(false);
    resetFormFiles();
    void loadList(page);
  };

  const handleRemoveExisting = async (attachment: WorkReportAttachment) => {
    if (view.type !== 'edit') return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      await deleteWorkReportAttachment(view.workReportId, attachment.attachmentId);
      setExistingFiles((prev) => prev.filter((item) => item.attachmentId !== attachment.attachmentId));
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    }
  };

  const detailAttachmentsMutable =
    view.type === 'detail' &&
    Boolean(detail?.canEdit) &&
    (detail?.status || 'DRAFT').toUpperCase() === 'DRAFT' &&
    !detail?.approvalInProgress;

  const handleDetailUpload = async (files: FileList | null) => {
    if (!files?.length || !detail || !detailAttachmentsMutable) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      setSaving(true);
      await uploadWorkReportAttachments(detail.workReportId, Array.from(files));
      setDetail(await fetchWorkReport(detail.workReportId));
      void loadList(page);
    } catch {
      notifyError('첨부파일 업로드에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDetailAttachmentDelete = async (attachment: WorkReportAttachment) => {
    if (!detail || !detailAttachmentsMutable) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteWorkReportAttachment(detail.workReportId, attachment.attachmentId);
      setDetail(await fetchWorkReport(detail.workReportId));
      void loadList(page);
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      notifyError('제목을 입력해 주세요.');
      return;
    }
    if (!reportDate) {
      notifyError('보고일을 입력해 주세요.');
      return;
    }
    if (!content.trim()) {
      notifyError('내용을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: title.trim(),
        reportDate,
        content: content.trim(),
      };
      let saved =
        view.type === 'edit'
          ? await updateWorkReport(view.workReportId, payload)
          : await createWorkReport(payload);
      if (pendingFiles.length > 0) {
        await uploadWorkReportAttachments(saved.workReportId, pendingFiles);
        saved = await fetchWorkReport(saved.workReportId);
      }
      setDetail(saved);
      resetFormFiles();
      setView({ type: 'detail', workReportId: saved.workReportId });
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: WorkReport) => {
    if (!window.confirm('이 업무 보고서를 삭제할까요?')) return;
    try {
      await deleteWorkReport(row.workReportId);
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '삭제에 실패했습니다.'
      );
    }
  };

  const handleApproveDirect = async () => {
    if (!detail) return;
    if (!window.confirm('바로 승인할까요? 승인 후에는 수정·삭제가 잠깁니다.')) return;
    try {
      setApproving(true);
      setDetail(await approveWorkReport(detail.workReportId));
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail?.canRevoke) return;
    if (!window.confirm('승인을 철회할까요? 다시 작성중 상태로 돌아갑니다.')) return;
    try {
      setApproving(true);
      setDetail(await revokeWorkReport(detail.workReportId));
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      const name = detail.title?.trim() || String(detail.workReportId);
      await downloadWorkReportPdf(detail.workReportId, `업무보고서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const renderEditActions = () => (
    <div className="leave-panel__form-actions">
      <button type="button" className="leave-panel__btn is-ghost" onClick={backToList}>
        취소
      </button>
      <button type="submit" className="leave-panel__btn is-primary" disabled={saving}>
        {saving ? '저장 중...' : view.type === 'edit' ? '수정 저장' : '등록'}
      </button>
    </div>
  );

  const renderDetailActions = () => {
    if (!detail) return null;
    return (
      <div className="leave-panel__detail-actions">
        {detail.status === 'APPROVED' ? (
          <button
            type="button"
            className="leave-panel__btn is-primary"
            disabled={pdfDownloading}
            onClick={() => void handleDownloadPdf()}
          >
            {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
          </button>
        ) : null}
        {detail.canRevoke ? (
          <button
            type="button"
            className="leave-panel__btn is-danger"
            disabled={approving}
            onClick={() => void handleRevoke()}
          >
            {approving ? '철회 중...' : '승인 철회'}
          </button>
        ) : null}
        <ApprovalCancelButton
          detail={detail}
          className="leave-panel__btn is-ghost"
          disabled={approving}
          onCancelled={async () => {
            setDetail(await fetchWorkReport(detail.workReportId));
            void loadList(page);
          }}
        />
        {detail.canRequestApproval ? (
          <button
            type="button"
            className="leave-panel__btn is-ghost"
            onClick={() => setApprovalOpen(true)}
          >
            결재 요청
          </button>
        ) : null}
        {detail.canApproveDirect && canApproveAsManager ? (
          <button
            type="button"
            className="leave-panel__btn is-primary"
            disabled={approving}
            onClick={() => void handleApproveDirect()}
          >
            {approving ? '승인 중...' : '승인'}
          </button>
        ) : null}
        {detail.canEdit ? (
          <button
            type="button"
            className="leave-panel__btn is-ghost"
            onClick={() => openEdit(detail)}
          >
            수정
          </button>
        ) : null}
        {detail.canDelete ? (
          <button
            type="button"
            className="leave-panel__btn is-danger"
            onClick={() => void handleDelete(detail)}
          >
            삭제
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <main className="leave-panel">
      <header className="leave-panel__head">
        <div>
          <p className="leave-panel__eyebrow">Work Report</p>
          <h2>업무 보고서</h2>
          <p className="leave-panel__guide">
            작성중은 본인만 보이고, 승인된 건은 같은 회사 멤버에게 공유됩니다. 첨부파일·결재도 지원합니다.
          </p>
        </div>
        <div className="leave-panel__head-actions">
          {view.type === 'list' ? (
            <button type="button" className="leave-panel__btn is-primary" onClick={openCreate}>
              작성하기
            </button>
          ) : (
            <button type="button" className="leave-panel__btn is-ghost" onClick={backToList}>
              목록으로
            </button>
          )}
        </div>
      </header>

      {error ? <p className="leave-panel__error">{error}</p> : null}

      {view.type === 'list' ? (
        <div className="leave-panel__body">
          {loading ? (
            <p className="leave-panel__hint">불러오는 중...</p>
          ) : rows.length === 0 ? (
            <div className="leave-panel__empty">
              <strong>보고서가 없습니다</strong>
              <p>일일/주간 업무 보고서를 작성해 보세요.</p>
              <button type="button" className="leave-panel__btn is-primary" onClick={openCreate}>
                작성하기
              </button>
            </div>
          ) : (
            <div className="paged-list">
              <div className="paged-list__scroll">
                <table className="leave-panel__table">
                  <thead>
                    <tr>
                      <th>제목</th>
                      <th style={{ width: '110px' }}>보고일</th>
                      <th style={{ width: '120px' }}>작성자</th>
                      <th style={{ width: '90px' }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.workReportId} onClick={() => void openDetail(row.workReportId)}>
                        <td className="is-title">{row.title}</td>
                        <td>{formatDate(row.reportDate)}</td>
                        <td>{row.authorName || '-'}</td>
                        <td>
                          <span
                            className={`leave-panel__status is-${workReportStatusClass(
                              row.status,
                              row.approvalInProgress
                            )}`}
                          >
                            {workReportStatusLabel(row.status, row.approvalInProgress)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                loading={loading}
                onChange={(next) => void loadList(next)}
              />
            </div>
          )}
        </div>
      ) : null}

      {(view.type === 'create' || view.type === 'edit') && (
        <form className="leave-panel__form" onSubmit={(e) => void handleSubmit(e)}>
          {renderEditActions()}
          <label className="leave-panel__field">
            <span>제목</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="예: 9월 1주차 업무 보고"
              required
            />
          </label>
          <label className="leave-panel__field">
            <span>보고일</span>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              required
            />
          </label>
          <label className="leave-panel__field">
            <span>내용</span>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              maxLength={20000}
              placeholder="진행 업무, 이슈, 다음 계획을 작성하세요"
              required
            />
          </label>

          <div className="bulletin-panel__files leave-panel__files">
            <div className="bulletin-panel__files-head">
              <strong>첨부파일</strong>
              <span>최대 10개</span>
            </div>
            {existingFiles.length > 0 ? (
              <ul className="bulletin-panel__file-list">
                {existingFiles.map((file) => (
                  <li key={file.attachmentId}>
                    <button
                      type="button"
                      className="bulletin-panel__file-link"
                      onClick={() =>
                        view.type === 'edit'
                          ? void downloadWorkReportAttachment(
                              view.workReportId,
                              file.attachmentId,
                              file.fileName
                            )
                          : undefined
                      }
                    >
                      {file.fileName}
                      {file.fileSize != null ? ` (${formatSize(file.fileSize)})` : ''}
                    </button>
                    <button
                      type="button"
                      className="bulletin-panel__file-remove"
                      onClick={() => void handleRemoveExisting(file)}
                    >
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {pendingFiles.length > 0 ? (
              <ul className="bulletin-panel__file-list is-pending">
                {pendingFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <span>
                      {file.name} ({formatSize(file.size)})
                    </span>
                    <button
                      type="button"
                      className="bulletin-panel__file-remove"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                    >
                      제거
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <label className="bulletin-panel__attach">
              파일 선택
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  const next = Array.from(e.target.files ?? []);
                  const room = Math.max(0, 10 - existingFiles.length - pendingFiles.length);
                  setPendingFiles((prev) => [...prev, ...next].slice(0, prev.length + room));
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          {renderEditActions()}
        </form>
      )}

      {view.type === 'detail' && detail ? (
        <article className="leave-panel__detail">
          <div className="leave-panel__detail-head">
            <div>
              <span
                className={`leave-panel__status is-${workReportStatusClass(
                  detail.status,
                  detail.approvalInProgress
                )}`}
              >
                {workReportStatusLabel(detail.status, detail.approvalInProgress)}
              </span>
              <h3>{detail.title}</h3>
              <p>
                {detail.authorName || '-'} · 보고일 {formatDate(detail.reportDate)}
                {detail.approverName ? ` · 승인자 ${detail.approverName}` : ''}
              </p>
              {detail.approvalInProgress ? (
                <p className="leave-panel__hint" style={{ marginTop: 8 }}>
                  결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
                </p>
              ) : null}
            </div>
            {renderDetailActions()}
          </div>
          <div className="leave-panel__content">{detail.content}</div>

          {(detail.attachments && detail.attachments.length > 0) || detailAttachmentsMutable ? (
            <div className="bulletin-panel__detail-files" style={{ marginTop: 18 }}>
              <div className="bulletin-panel__files-head">
                <strong>첨부파일</strong>
                {detailAttachmentsMutable ? (
                  <label className="bulletin-panel__attach">
                    파일 추가
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      disabled={saving}
                      onChange={(e) => void handleDetailUpload(e.target.files)}
                    />
                  </label>
                ) : null}
              </div>
              {(detail.attachments ?? []).length > 0 ? <ul>
                {(detail.attachments ?? []).map((file) => (
                  <li key={file.attachmentId}>
                    <button
                      type="button"
                      onClick={() =>
                        void downloadWorkReportAttachment(
                          detail.workReportId,
                          file.attachmentId,
                          file.fileName
                        )
                      }
                    >
                      {file.fileName}
                      {file.fileSize != null ? ` (${formatSize(file.fileSize)})` : ''}
                    </button>
                    {detailAttachmentsMutable ? (
                      <button
                        type="button"
                        className="bulletin-panel__btn is-danger"
                        disabled={saving}
                        onClick={() => void handleDetailAttachmentDelete(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul> : <p className="leave-panel__hint">등록된 첨부파일이 없습니다.</p>}
            </div>
          ) : null}

          {renderDetailActions()}
          {detail.canRequestApproval || detail.canApproveDirect ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${detail.title} 결재`}
              targetType={APPROVAL_TARGET_WORK_REPORT}
              targetId={detail.workReportId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                void openDetail(detail.workReportId);
              }}
            />
          ) : null}
        </article>
      ) : null}
    </main>
  );
}

export default WorkReportPanel;
